import { Home, Heart, Stethoscope, Compass } from "lucide-react";
import { QUESTIONS } from "@/data/questions";
import { LEGACY_QUESTIONS, FAMILY_QUESTIONS, MEDICARE_QUESTIONS } from "@/data/pathwayQuestions";

export const PATHWAY_ORDER = ["legacy", "family", "medicare", "complete"];

export const PATHWAYS = {
  legacy: {
    key: "legacy",
    route: "/checkup/legacy",
    bookingKey: "legacy",
    label: "Legacy Checkup",
    menuLabel: "Protect My Legacy",
    shortLabel: "Legacy",
    icon: Home,
    image: "/pathways/legacy-home",
    imageAlt: "Luxury modern home glowing with warm light at dusk",
    card: { title: "Protect My Legacy", description: "Wills, trusts, probate, home/property and beneficiaries.", tagline: "Clarity today. Peace tomorrow." },
    intro: {
      eyebrow: "Legacy Checkup™",
      headline: ["A Clearer Picture", "of Your Legacy."],
      text: "Answer a few simple questions about wills, trusts, your home and beneficiaries to discover what you already have in place—and what may need a closer look.",
      cta: "Start My Legacy Checkup",
      minutes: "About 2 minutes",
    },
    questions: LEGACY_QUESTIONS,
    resultType: "score",
    scoreLabel: "Your Legacy Readiness Score",
    shareLabel: "LEGACY READINESS",
    review: {
      title: "Legacy Review",
      question: "Would it be helpful to review your results and talk through what, if anything, may deserve a closer look?",
      cta: "SCHEDULE A LEGACY REVIEW",
      duration: "30-minute conversation",
      description: "In this 30-minute review, we'll walk through what you shared in your Legacy Checkup, clarify areas that may deserve a closer look, and determine whether any next steps make sense for you.",
    },
    seo: { title: "Protect My Legacy — Legacy Checkup™", description: "A short, educational checkup about wills, trusts, probate, your home and beneficiaries. Discover what you have in place and what may deserve a closer look." },
  },
  family: {
    key: "family",
    route: "/checkup/family",
    bookingKey: "family",
    label: "Family Protection Checkup",
    menuLabel: "Protect My Family",
    shortLabel: "Family",
    icon: Heart,
    image: "/pathways/family",
    imageAlt: "Parents embracing their daughter at golden hour",
    card: { title: "Protect My Family", description: "Life insurance, living benefits and final expense planning.", tagline: "Love today. Security tomorrow." },
    intro: {
      eyebrow: "Family Protection Checkup",
      headline: ["Understand How Your", "Family Is Protected."],
      text: "Seven simple questions about who depends on you, the coverage you have today, and what you'd most want a protection plan to accomplish. Educational, personal, and pressure-free.",
      cta: "Start My Family Protection Checkup",
      minutes: "About 2 minutes",
    },
    questions: FAMILY_QUESTIONS,
    resultType: "recap",
    results: {
      title: "Your Family Protection Checkup Is Complete",
      text: "Based on your answers, here are a few areas that may be worth reviewing.",
      areasTitle: "Areas That May Be Worth Reviewing",
      disclaimer: "This checkup is educational. It does not determine how much coverage anyone needs, recommend a specific insurance product, or guarantee any outcome. Insurance products are available only where Legacy Wave Consulting is appropriately licensed. A review simply helps you understand your situation before deciding whether any next step makes sense.",
    },
    review: {
      title: "Family Protection Review",
      question: "Would it be helpful to review your results with a licensed agent and see what, if anything, may need attention?",
      cta: "SCHEDULE A FAMILY PROTECTION REVIEW",
      duration: "20-minute conversation",
      description: "In this 20-minute review, we'll walk through what you shared, clarify areas that may deserve a closer look, and determine whether any next steps make sense.",
    },
    seo: { title: "Protect My Family — Family Protection Checkup", description: "An educational checkup about life insurance, living benefits and final expense planning. Understand where you are before deciding what makes sense next." },
  },
  medicare: {
    key: "medicare",
    route: "/checkup/medicare",
    bookingKey: "medicare",
    label: "Medicare Checkup",
    menuLabel: "Understand My Medicare",
    shortLabel: "Medicare",
    icon: Stethoscope,
    image: "/pathways/medicare-family",
    imageAlt: "Three generations of a family laughing together on a porch",
    imagePosition: "center top",
    card: { title: "Understand My Medicare", description: "Eligibility, current coverage, doctors, prescriptions and coverage reviews.", tagline: "Right coverage. A brighter tomorrow." },
    intro: {
      eyebrow: "Medicare Checkup",
      headline: ["Understand Your", "Medicare Coverage."],
      text: "Five simple questions about your eligibility, current coverage, and what you'd like help understanding. Educational only—no plan is evaluated or recommended here.",
      cta: "Start My Medicare Checkup",
      minutes: "About 1 minute",
    },
    questions: MEDICARE_QUESTIONS,
    resultType: "recap",
    results: {
      title: "Your Medicare Checkup is complete.",
      text: "Based on your answers, there may be areas of your coverage worth reviewing. A licensed agent can help you understand your current coverage and, when appropriate, discuss available options.",
      topicsTitle: "Topics You Asked About",
      disclaimer: "Legacy Wave Consulting is not connected with or endorsed by the U.S. government or the federal Medicare program. The Medicare Checkup is educational only. It does not evaluate, compare, or recommend any specific Medicare plan, and it does not determine eligibility for any program. Contacting a licensed agent is entirely your choice.",
    },
    review: {
      title: "Medicare Coverage Review",
      question: "Would it be helpful to review your coverage with a licensed agent?",
      cta: "SCHEDULE A MEDICARE COVERAGE REVIEW",
      duration: "30-minute conversation",
      description: "In this review, a licensed agent will help you understand your current Medicare coverage and, when appropriate, discuss available options. The purpose is to understand your situation first—nothing is decided in advance.",
    },
    seo: { title: "Understand My Medicare — Medicare Checkup", description: "A neutral, educational Medicare checkup: eligibility, current coverage, doctors, prescriptions and coverage reviews. No plan recommendations." },
  },
  complete: {
    key: "complete",
    route: "/checkup",
    bookingKey: "legacy",
    label: "Complete Legacy Checkup",
    menuLabel: "Complete Legacy Checkup",
    shortLabel: "Full Checkup",
    icon: Compass,
    image: "/pathways/compass",
    imageAlt: "Woman looking out over a sunrise horizon from a mountain overlook",
    card: { title: "I'm Not Sure Where to Start", description: "Take the Complete Legacy Checkup to identify areas that may be worth reviewing.", tagline: "Answers today. A stronger tomorrow." },
    intro: {
      eyebrow: "Complete Legacy Checkup™",
      headline: ["A Clearer Picture", "of Your Legacy."],
      text: "Answer a few simple questions to discover what you already have in place—and what may need a closer look.",
      cta: "Start My Free Checkup",
      minutes: "About 3 minutes",
    },
    questions: QUESTIONS,
    resultType: "score",
    scoreLabel: "Your Legacy Readiness Score",
    shareLabel: "LEGACY READINESS",
    review: {
      title: "Legacy Review",
      question: "Would it be helpful to review your results and talk through what, if anything, may deserve a closer look?",
      cta: "SCHEDULE A LEGACY REVIEW",
      duration: "30-minute conversation",
      description: "In this 30-minute review, we'll walk through what you shared in your Legacy Checkup, clarify areas that may deserve a closer look, and determine whether any next steps make sense for you.",
    },
    seo: { title: "Complete Legacy Checkup™ — Free 3-Minute Assessment", description: "Answer 10 gentle questions and discover your Legacy Readiness Score with personalized areas to review. Free, private, and educational." },
  },
};

export const BOOKING_PATHWAYS = ["legacy", "family", "medicare"];
